import React from "react";
import moment from "moment-timezone";
import { MySQLAdminData, RedisAdminData } from "../types";

interface AlarmTabProps {
  mysqlData: MySQLAdminData;
  redisData: RedisAdminData;
}

export const AlarmTab: React.FC<AlarmTabProps> = ({ mysqlData, redisData }) => {
  return (
    <div className="space-y-8">
      {/* 알림 설정 현황 KPI */}
      <section className="glass-card">
        <h2 className="text-3xl font-bold tracking-tight text-white/90 glow-text mb-6">
          알림 설정 현황
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="마케팅 알림 동의"
            value={`${mysqlData.alarmStats.totalMarketingUsers.toLocaleString()}명`}
            subtitle={`전체 대비 ${mysqlData.alarmStats.totalMarketingUsersRate}%`}
            accent="blue"
          />
          <KPICard
            title="종목 알림 전송 동의"
            value={`${mysqlData.alarmStats.totalWatchlistAlarmUsers.toLocaleString()}명`}
            subtitle={`전체 대비 ${mysqlData.alarmStats.totalWatchlistAlarmUsersRate}%`}
            accent="cyan"
          />
          <KPICard
            title="리포트 알림 동의"
            value={`${mysqlData.alarmStats.totalReportAlarmUsers.toLocaleString()}명`}
            subtitle={`전체 대비 ${mysqlData.alarmStats.totalReportAlarmUsersRate}%`}
            accent="violet"
          />
          <KPICard
            title="특정 종목 알림 신청자"
            value={`${mysqlData.stockAlarmStats.totalStockAlarmUsers.toLocaleString()}명`}
            subtitle={`전체 대비 ${mysqlData.stockAlarmStats.totalStockAlarmUsersRate}%`}
            accent="pink"
          />
        </div>
      </section>

      {/* 기간별 알림 동의율 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            마케팅 알림 동의율
            <span className="text-sm text-white/60 font-normal ml-2">
              (신규 가입자 대비)
            </span>
          </h2>
          <div className="space-y-4">
            <PeriodAlarmStat
              period="최근 1일"
              count={mysqlData.alarmStats.marketingUsers1d}
              rate={mysqlData.alarmStats.marketingUsersRate1d}
              color="from-blue-500 to-cyan-500"
            />
            <PeriodAlarmStat
              period="최근 7일"
              count={mysqlData.alarmStats.marketingUsers7d}
              rate={mysqlData.alarmStats.marketingUsersRate7d}
              color="from-blue-600 to-cyan-600"
            />
            <PeriodAlarmStat
              period="최근 30일"
              count={mysqlData.alarmStats.marketingUsers30d}
              rate={mysqlData.alarmStats.marketingUsersRate30d}
              color="from-blue-700 to-cyan-700"
            />
          </div>
        </section>

        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            종목 알림 동의율
            <span className="text-sm text-white/60 font-normal ml-2">
              (신규 가입자 대비)
            </span>
          </h2>
          <div className="space-y-4">
            <PeriodAlarmStat
              period="최근 1일"
              count={mysqlData.alarmStats.watchlistUsers1d}
              rate={mysqlData.alarmStats.watchlistUsersRate1d}
              color="from-violet-500 to-purple-500"
            />
            <PeriodAlarmStat
              period="최근 7일"
              count={mysqlData.alarmStats.watchlistUsers7d}
              rate={mysqlData.alarmStats.watchlistUsersRate7d}
              color="from-violet-600 to-purple-600"
            />
            <PeriodAlarmStat
              period="최근 30일"
              count={mysqlData.alarmStats.watchlistUsers30d}
              rate={mysqlData.alarmStats.watchlistUsersRate30d}
              color="from-violet-700 to-purple-700"
            />
          </div>
        </section>

        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            리포트 알림 동의율
            <span className="text-sm text-white/60 font-normal ml-2">
              (신규 가입자 대비)
            </span>
          </h2>
          <div className="space-y-4">
            <PeriodAlarmStat
              period="최근 1일"
              count={mysqlData.alarmStats.reportUsers1d}
              rate={mysqlData.alarmStats.reportUsersRate1d}
              color="from-pink-500 to-rose-500"
            />
            <PeriodAlarmStat
              period="최근 7일"
              count={mysqlData.alarmStats.reportUsers7d}
              rate={mysqlData.alarmStats.reportUsersRate7d}
              color="from-pink-600 to-rose-600"
            />
            <PeriodAlarmStat
              period="최근 30일"
              count={mysqlData.alarmStats.reportUsers30d}
              rate={mysqlData.alarmStats.reportUsersRate30d}
              color="from-pink-700 to-rose-700"
            />
          </div>
        </section>
      </div>

      {/* 종목 알림 현황 */}
      <section className="glass-card">
        <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
          종목 알림 현황
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-sub-card">
            <div className="text-white/70 text-sm tracking-wide font-medium mb-1">
              총 종목 알림 신청 건수
            </div>
            <div className="text-2xl font-bold text-white/95 mb-1">
              {mysqlData.stockAlarmStats.totalStockAlarmCount.toLocaleString()}
            </div>
            <div className="text-white/60 text-xs">전체 신청 건수</div>
          </div>
          <div className="glass-sub-card">
            <div className="text-white/70 text-sm tracking-wide font-medium mb-1">
              평균 알림 종목 개수
            </div>
            <div className="text-2xl font-bold text-white/95 mb-1">
              {parseFloat(
                mysqlData.stockAlarmStats.avgStockAlarmPerUser
              ).toFixed(1)}
            </div>
            <div className="text-white/60 text-xs">사용자당 평균</div>
          </div>
          <div className="glass-sub-card">
            <div className="text-white/70 text-sm tracking-wide font-medium mb-1">
              최근 30일 신청자
            </div>
            <div className="text-2xl font-bold text-white/95 mb-1">
              {mysqlData.stockAlarmStats.stockAlarmUsers30d.toLocaleString()}
            </div>
            <div className="text-white/60 text-xs">
              신규 가입자 대비{" "}
              {mysqlData.stockAlarmStats.stockAlarmUsersRate30d}%
            </div>
          </div>
        </div>
      </section>

      {/* 인기 알림 종목 & 전략 알림 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            인기 알림 종목 TOP 10
            <span className="text-base text-white/60 font-normal ml-2">
              (최근 30일)
            </span>
          </h2>
          <TopAlarmStocksTable data={mysqlData.topAlarmStocks} />
        </section>

        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            전략 알림 신청 목록
          </h2>
          <StrategyTable data={redisData.strategy} />
        </section>
      </div>
    </div>
  );
};

function KPICard({
  title,
  value,
  subtitle,
  accent = "blue",
}: {
  title: string;
  value: string;
  subtitle: string;
  accent?: "blue" | "cyan" | "violet" | "pink" | "emerald";
}) {
  const accentColors = {
    blue: "from-blue-400/20 to-cyan-400/20 border-blue-400/30 shadow-blue-400/20",
    cyan: "from-cyan-400/20 to-teal-400/20 border-cyan-400/30 shadow-cyan-400/20",
    violet:
      "from-violet-400/20 to-purple-400/20 border-violet-400/30 shadow-violet-400/20",
    pink: "from-pink-400/20 to-rose-400/20 border-pink-400/30 shadow-pink-400/20",
    emerald:
      "from-emerald-400/20 to-green-400/20 border-emerald-400/30 shadow-emerald-400/20",
  };

  return (
    <div className={`glass-kpi ${accentColors[accent]} border border-white/10`}>
      <div className="text-white/70 text-sm tracking-wide font-medium mb-1">
        {title}
      </div>
      <div className="text-2xl font-bold text-white/95 mb-1">{value}</div>
      <div className="text-white/60 text-xs">{subtitle}</div>
    </div>
  );
}

function PeriodAlarmStat({
  period,
  count,
  rate,
  color,
}: {
  period: string;
  count: number;
  rate: string;
  color: string;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-12 rounded-full bg-gradient-to-b ${color}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white/80 font-medium text-sm">{period}</span>
            <span className="text-cyan-300 font-bold text-lg">{rate}%</span>
          </div>
          <div className="text-white/60 text-sm">
            {count.toLocaleString()}명 동의
          </div>
        </div>
      </div>
    </div>
  );
}

function TopAlarmStocksTable({
  data,
}: {
  data: Array<{
    ticker: string;
    stockName: string;
    userCount: number;
    totalCount: number;
  }>;
}) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );
  }

  return (
    <div className="max-h-96 glass-table-container scrollable-table">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
          <tr>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20 w-16">
              순위
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              티커
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              종목명
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              알림 신청자
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((stock, index) => (
            <tr
              key={stock.ticker}
              className={index % 2 === 0 ? "bg-white/2" : ""}
            >
              <td className="px-4 py-3 border-b border-white/10">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0
                      ? "bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900"
                      : index === 1
                      ? "bg-gradient-to-r from-gray-300 to-gray-500 text-gray-900"
                      : index === 2
                      ? "bg-gradient-to-r from-amber-600 to-amber-800 text-amber-100"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {index + 1}
                </div>
              </td>
              <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                {stock.ticker}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base font-medium">
                {stock.stockName || "-"}
              </td>
              <td className="px-4 py-3 text-violet-300 font-bold border-b border-white/10 text-base">
                {stock.userCount.toLocaleString()}명
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 전략 알림 신청 테이블 - 내부 스크롤
function StrategyTable({ data }: { data: any }) {
  if (!data || Object.keys(data).length === 0)
    return (
      <div className="text-center py-8">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );
  const rows = Object.entries(data).map(([email, value]: any) => {
    let v;
    try {
      v = typeof value === "string" ? JSON.parse(value) : value;
    } catch {
      v = value;
    }
    return {
      email,
      strategy: Array.isArray(v.strategyAlarm)
        ? v.strategyAlarm.join(", ")
        : "",
      onoff: v.strategyAlarmOnOff ? "ON" : "OFF",
      createdAt: v.createdAt,
      updatedAt: v.updatedAt,
    };
  });
  // 생성일 내림차순 정렬 (생성일 없는 데이터는 아래로)
  rows.sort((a, b) => {
    const aDate = a.createdAt ? a.createdAt.trim() : "";
    const bDate = b.createdAt ? b.createdAt.trim() : "";
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return bDate.localeCompare(aDate);
  });
  return (
    <div>
      <div className="max-h-96 glass-table-container scrollable-table">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
            <tr>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                이메일
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                전략
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                알림 ON/OFF
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                생성일
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.email}
                className={index % 2 === 0 ? "bg-white/2" : ""}
              >
                <td className="px-4 py-3 text-white/70 font-mono text-xs border-b border-white/10">
                  {row.email}
                </td>
                <td className="px-4 py-3 text-white/80 border-b border-white/10 text-sm">
                  {row.strategy}
                </td>
                <td className="px-4 py-3 border-b border-white/10">
                  <span
                    className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${
                      row.onoff === "ON"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30"
                        : "bg-red-500/20 text-red-300 border border-red-400/30"
                    }`}
                  >
                    {row.onoff}
                  </span>
                </td>
                <td className="px-4 py-3 text-violet-300 font-mono text-xs border-b border-white/10">
                  {moment(row.createdAt).format("YY.M.D HH:mm")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
